import React, { useEffect, useMemo, useRef, useState } from 'react';
import { webNavigate } from '../../services/navigate.service';
import { getListProductWithLocation } from '../../services/items.services';
import { deleteTransfer, getDetailTransfer, getTransferLineCosts, TransferPayload, updateTransfer } from '../../services/transfer.service';
import { showDeleteToast, showToast } from '../../services/toast.service';
import { showApiError } from '../../services/apiError';
import { useResponsive } from '../../hook/useResponsive';
import { useDebounceEffect } from '../../hook/useDebounceEffect';
import {
  Button,
  ConfirmDialog,
  DocumentHeader,
  EmptyState,
  ErrorBanner,
  FieldMessage,
  IconButton,
  LinesCard,
  MenuItem,
  NextStep,
  NumberField,
  PageFrame,
  PhoneLabel,
  PhoneLine,
  PickerRow,
  ProductCell,
  ProductPicker,
  ReadValue,
  SaveBar,
  SelectField,
  Skeleton,
  TotalsList,
  TRANSFER_STATUS,
  card,
  defaultUnitId,
  money,
  quantity,
  tableStyle,
  td,
  th,
  transferStatusOf,
  unitsOfProduct,
  useLanguage,
} from '../../components/purchasing';
import { parseNumber, priceText, qtyText } from '../Order/orderLineRules';
import {
  LineCost,
  TransferLine,
  checkReceived,
  checkSent,
  costKey,
  costOf,
  costRequest,
  draftChanges,
  lineOfDetail,
  lineOfPick,
  linesPayload,
  unitName,
  withLineCost,
  withUnit,
} from './transferLines';
import { transferFacts, useTransferPrint } from './transferShared';

const PICK_PAGE = 20;
/** The most lines POST /inventory-transfers/costs takes at once. */
const COST_BATCH = 200;

type Mode = 'edit' | 'receive' | 'read';

/** One page for a transfer in every state: the draft is edited here, the receiving site counts and receives it. */
const TransferDetail = () => {
  const preLanguage = localStorage.getItem('language');
  const companyId = localStorage.getItem('companyId') || '';
  const headerSite = localStorage.getItem('locationId') || '';
  const id: string = window.location.pathname.split('/').pop() || '';
  // The list opens a transfer of other sites from one of its own (?site=), as the API only shows it to them.
  const urlSite = new URLSearchParams(location.search).get('site') || '';
  const { isMobile: phone } = useResponsive();
  const { language, t } = useLanguage();

  const [transfer, setTransfer] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [lines, setLines] = useState<TransferLine[]>([]);
  const [saved, setSaved] = useState<TransferLine[]>([]);
  const [flagged, setFlagged] = useState(false);
  const [notice, setNotice] = useState('');
  const [lineSearch, setLineSearch] = useState('');
  const [busy, setBusy] = useState<'' | 'draft' | 'send' | 'receive' | 'reject' | 'cancel' | 'delete'>('');
  const [confirm, setConfirm] = useState<'' | 'receive' | 'reject' | 'cancel' | 'delete' | 'leave'>('');

  // Add products
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickSearch, setPickSearch] = useState('');
  const [pickTerm, setPickTerm] = useState('');
  const [picks, setPicks] = useState<any[]>([]);
  const [pickCount, setPickCount] = useState(0);
  const [pickPage, setPickPage] = useState(1);
  const [picksLoading, setPicksLoading] = useState(false);
  const [selected, setSelected] = useState<Map<string, string>>(() => new Map());
  const pickSeq = useRef(0);
  const costSeq = useRef(0);

  const statusId = Number(transfer?.status_id);
  const fromId = String(transfer?.from_location_id ?? '');
  const toId = String(transfer?.to_location_id ?? '');
  const fromName = transfer?.fromLocation?.name ?? '';
  const toName = transfer?.toLocation?.name ?? '';
  // Only someone working at one of the two sites changes the transfer.
  const atSite = !!headerSite && (headerSite === fromId || headerSite === toId);
  const mode: Mode = statusId === TRANSFER_STATUS.Draft && atSite ? 'edit' : statusId === TRANSFER_STATUS.Sent && headerSite === toId ? 'receive' : 'read';
  const editable = mode === 'edit';
  const receiving = mode === 'receive';
  const title = transfer ? t('Transfer #{number}', { number: transfer.internal_number }) : t('Transfer');

  /* ---------------- Loading ---------------- */

  const apply = (raw: any) => {
    const next = (raw?.inventoryTransferDetail ?? []).map(lineOfDetail);
    setTransfer(raw);
    setLines(next);
    setSaved(next);
    setFlagged(false);
    setNotice('');
  };

  const load = async () => {
    setLoading(true);
    setFailed(false);
    try {
      const viewSite = urlSite || headerSite;
      let raw = (await getDetailTransfer(id, viewSite))?.data?.data;
      // A draft is counted out of the site it leaves, so its lines show that site's stock.
      if (raw && Number(raw.status_id) === TRANSFER_STATUS.Draft && raw.from_location_id && raw.from_location_id !== viewSite) {
        raw = (await getDetailTransfer(id, raw.from_location_id))?.data?.data ?? raw;
      }
      apply(raw);
    } catch (error: any) {
      setFailed(true);
      showApiError(error, { language });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id === 'new') {
      // Transfers are created from the list's New transfer dialog.
      webNavigate(`/${preLanguage}/transfer?new=1`);
      return;
    }
    load();
    const onToken = () => load();
    document.addEventListener('newToken', onToken);
    return () => document.removeEventListener('newToken', onToken);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  /* ---------------- Lines ---------------- */

  const sentChecks = useMemo(() => new Map(lines.map((line) => [line.key, checkSent(line, flagged)])), [lines, flagged]);
  const receivedChecks = useMemo(() => new Map(lines.map((line) => [line.key, checkReceived(line, flagged)])), [lines, flagged]);
  const changes = editable ? draftChanges(lines, saved) : [];
  const counted = receiving ? lines.filter((line) => receivedChecks.get(line.key)?.counted) : [];
  const shortLines = counted.filter((line) => (receivedChecks.get(line.key)?.notice ?? '') === '{qty} short');
  const dirty = editable ? changes.length > 0 : receiving ? lines.some((line) => line.received.trim() !== '') : false;
  const attention = editable
    ? lines.filter((line) => sentChecks.get(line.key)?.error || sentChecks.get(line.key)?.costError).length
    : receiving
      ? lines.filter((line) => receivedChecks.get(line.key)?.error).length
      : 0;

  const term = lineSearch.trim().toLowerCase();
  const shown = term ? lines.filter((line) => `${line.name} ${line.sku}`.toLowerCase().includes(term)) : lines;

  const patch = (key: string, change: Partial<TransferLine>) => {
    setNotice('');
    setLines((prev) => prev.map((line) => (line.key === key ? { ...line, ...change } : line)));
  };
  const patchLine = (key: string, next: (line: TransferLine) => TransferLine) => {
    setNotice('');
    setLines((prev) => prev.map((line) => (line.key === key ? next(line) : line)));
  };
  const removeLine = (key: string) => setLines((prev) => prev.filter((line) => line.key !== key));
  const discard = () => {
    setLines(saved);
    setFlagged(false);
    setNotice('');
  };
  const everythingArrived = () => setLines((prev) => prev.map((line) => ({ ...line, received: qtyText(line.sentQty) })));

  /** Shows every line's problem and moves to the first one, which may be far below the header. */
  const flagAndFocus = (column: string) => {
    setFlagged(true);
    window.setTimeout(() => {
      const first = document.querySelector<HTMLInputElement>(`[data-lines] input[data-col="${column}"][aria-invalid="true"]`);
      first?.scrollIntoView({ block: 'center' });
      first?.focus();
    }, 0);
  };

  /* ---------------- Costs ---------------- */

  // A draft's lines are priced at the sending site (its layers, in-use first, else the supplier); a change of unit or qty asks again.
  const pendingCosts = editable && fromId ? lines.filter((line) => line.costFor !== costKey(line, fromId)) : [];
  const pendingKey = pendingCosts.map((line) => costKey(line, fromId)).join(',');

  const fetchCosts = async (asked: TransferLine[]): Promise<Map<string, LineCost>> => {
    const answers = new Map<string, LineCost>();
    for (let start = 0; start < asked.length; start += COST_BATCH) {
      const chunk = asked.slice(start, start + COST_BATCH);
      const response: any = await getTransferLineCosts({ locationId: fromId, lines: chunk.map(costRequest) });
      const rows: LineCost[] = response?.data?.data ?? [];
      chunk.forEach((line, index) => rows[index] && answers.set(costKey(line, fromId), rows[index]));
    }
    return answers;
  };
  // An answer applies only to a line still as it was asked about.
  const withCosts = (current: TransferLine[], answers: Map<string, LineCost>) =>
    current.map((line) => {
      const key = costKey(line, fromId);
      const known = answers.get(key);
      return known ? withLineCost(line, known, key) : line;
    });

  useDebounceEffect(() => {
    if (!pendingKey) return;
    const mine = ++costSeq.current;
    fetchCosts(pendingCosts)
      .then((answers) => setLines((prev) => withCosts(prev, answers)))
      .catch((error: any) => {
        if (mine === costSeq.current) showApiError(error, { language });
      });
  }, [pendingKey], 300);

  /* ---------------- Values ---------------- */

  const sentValue = (list: TransferLine[]) => list.reduce((sum, line) => sum + costOf(line) * line.sentQty, 0);
  const totalRows = (() => {
    if (editable) {
      const value = lines.reduce((sum, line) => sum + (sentChecks.get(line.key)?.value ?? 0), 0);
      return [{ label: t('Value at cost'), value: money(value), strong: true }];
    }
    if (receiving) {
      const value = counted.reduce((sum, line) => sum + costOf(line) * (receivedChecks.get(line.key)?.qty ?? 0), 0);
      return [
        { label: t('Sent value'), value: money(sentValue(lines)) },
        { label: t('Received value'), value: money(value), strong: true },
      ];
    }
    if (statusId === TRANSFER_STATUS.Received) {
      return [
        { label: t('Sent value'), value: money(sentValue(lines)) },
        { label: t('Received value'), value: money(lines.reduce((sum, line) => sum + costOf(line) * line.receivedQty, 0)), strong: true },
      ];
    }
    return [{ label: t('Value at cost'), value: money(sentValue(lines)), strong: true }];
  })();

  /* ---------------- Actions ---------------- */

  const backToList = () => webNavigate(`/${preLanguage}/transfer`);
  const onBack = () => (dirty ? setConfirm('leave') : backToList());

  const payloadFor = (statusIdNext: number, list: TransferLine[], linesMode: 'edit' | 'receive' | 'keep'): TransferPayload => ({
    company_id: companyId,
    from_location_id: fromId,
    to_location_id: toId,
    reason_id: transfer?.reason_id ?? null,
    status_id: statusIdNext,
    inventoryTransferDetail: linesPayload(list, linesMode),
  });

  const blockingColumn = (list: TransferLine[]) => {
    const checks = list.map((line) => checkSent(line, true));
    return checks.some((check) => check.error) ? 'qty' : checks.some((check) => check.costError) ? 'cost' : '';
  };

  const save = async (send: boolean) => {
    if (busy) return;
    const blocked = blockingColumn(lines);
    if (blocked) {
      flagAndFocus(blocked);
      return;
    }
    if (send && lines.length === 0) {
      setNotice(t('Add at least one product before sending.'));
      return;
    }
    if (lines.some((line) => !line.productUOMId)) {
      setNotice(t('Choose a unit for every product'));
      return;
    }
    setBusy(send ? 'send' : 'draft');
    try {
      // Costs still being worked out come first, so what is sent is what the page shows.
      let current = lines;
      if (pendingCosts.length) {
        current = withCosts(lines, await fetchCosts(pendingCosts));
        setLines(current);
        const stillBlocked = blockingColumn(current);
        if (stillBlocked) {
          flagAndFocus(stillBlocked);
          return;
        }
      }
      await updateTransfer(id, payloadFor(send ? TRANSFER_STATUS.Sent : TRANSFER_STATUS.Draft, current, 'edit'), headerSite);
      showToast({ message: send ? t('Transfer sent to {site}', { site: toName }) : t('Draft saved'), type: 'success', duration: 5000 });
      await load();
    } catch (error: any) {
      showApiError(error, { language });
    } finally {
      setBusy('');
    }
  };

  const startReceive = () => {
    if (busy) return;
    if (lines.some((line) => !checkReceived(line, true).counted)) {
      flagAndFocus('received');
      return;
    }
    setConfirm('receive');
  };

  const receive = async () => {
    setBusy('receive');
    try {
      await updateTransfer(id, payloadFor(TRANSFER_STATUS.Received, lines, 'receive'), headerSite);
      setConfirm('');
      showToast({ message: t('Transfer received. Stock is updated at {site}.', { site: toName }), type: 'success', duration: 6000 });
      await load();
    } catch (error: any) {
      showApiError(error, { language });
    } finally {
      setBusy('');
    }
  };

  const close = async (next: 'reject' | 'cancel') => {
    setBusy(next);
    try {
      await updateTransfer(id, payloadFor(next === 'reject' ? TRANSFER_STATUS.Rejected : TRANSFER_STATUS.Cancelled, saved, 'keep'), headerSite);
      setConfirm('');
      showToast({ message: next === 'reject' ? t('Transfer rejected') : t('Transfer cancelled'), type: 'success', duration: 5000 });
      await load();
    } catch (error: any) {
      showApiError(error, { language });
    } finally {
      setBusy('');
    }
  };

  const removeDraft = async () => {
    setBusy('delete');
    try {
      await deleteTransfer(id, headerSite);
      setConfirm('');
      showDeleteToast(t('Transfer deleted'));
      backToList();
    } catch (error: any) {
      showApiError(error, { language });
    } finally {
      setBusy('');
    }
  };

  /* ---------------- Add products ---------------- */

  useDebounceEffect(() => setPickTerm(pickSearch.trim()), [pickSearch], 300);

  const loadPicks = async (page: number, append: boolean) => {
    const mine = ++pickSeq.current;
    setPicksLoading(true);
    try {
      // The sending site's products, with its stock and stock cost.
      const response: any = await getListProductWithLocation(page, PICK_PAGE, pickTerm, fromId);
      if (mine !== pickSeq.current) return;
      const rows: any[] = response?.data?.productWithCompany ?? [];
      setPicks((prev) => {
        if (!append) return rows;
        const known = new Set(prev.map((row) => String(row.id)));
        return [...prev, ...rows.filter((row) => !known.has(String(row.id)))];
      });
      setPickCount(Number(response?.data?.count) || 0);
      setPickPage(page);
    } catch (error: any) {
      if (mine === pickSeq.current) showApiError(error, { language });
    } finally {
      if (mine === pickSeq.current) setPicksLoading(false);
    }
  };

  useEffect(() => {
    if (pickerOpen) loadPicks(1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickerOpen, pickTerm]);

  const openPicker = () => {
    setPickSearch('');
    setPickTerm('');
    setSelected(new Map());
    setPickerOpen(true);
  };

  const onTransfer = new Set(lines.map((line) => line.productId));
  const pickerRows: PickerRow[] = picks.map((pick) => {
    const productId = String(pick.id);
    const units = unitsOfProduct(pick);
    const base = units.find((unit) => unit.isBase)?.name ?? '';
    const stock = Number(pick?.currentStock);
    return {
      id: productId,
      name: pick?.productName ?? '',
      sku: pick?.sku ?? null,
      group: [pick?.productGroup?.groupName, Number.isFinite(stock) ? t('On hand {qty}', { qty: `${quantity(stock)}${base ? ` ${base}` : ''}` }) : null].filter(Boolean).join(' · '),
      units: units.map((unit) => ({ id: unit.id, name: unit.name, taken: onTransfer.has(productId) })),
      selected: selected.has(productId),
      unitId: selected.get(productId) ?? defaultUnitId(units),
    };
  });

  const togglePick = (row: PickerRow) =>
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(row.id)) next.delete(row.id);
      else if (row.unitId) next.set(row.id, row.unitId);
      return next;
    });
  const pickUnit = (row: PickerRow, unitId: string) =>
    setSelected((prev) => {
      const next = new Map(prev);
      next.set(row.id, unitId);
      return next;
    });

  const addPicked = () => {
    const known = new Set(lines.map((line) => line.productId));
    const added: TransferLine[] = [];
    selected.forEach((unitId, productId) => {
      const pick = picks.find((row) => String(row.id) === productId);
      if (!pick || !unitId || known.has(productId)) return;
      known.add(productId);
      added.push(lineOfPick(pick, unitId));
    });
    setLines((prev) => [...prev, ...added]);
    // A just-added line is never hidden by the line search.
    setLineSearch('');
    setPickerOpen(false);
  };

  /* ---------------- Header ---------------- */

  const facts = transferFacts(transfer, t);
  const receiveButton = (
    <Button variant="primary" phone={phone} fullWidth={phone} disabled={!!busy} onClick={startReceive}>
      {t('Receive transfer')}
    </Button>
  );
  let nextStep: NextStep | null = null;
  if (statusId === TRANSFER_STATUS.Sent) {
    if (receiving) {
      nextStep = {
        tone: 'progress',
        text: t('Count what arrives at {site}, then receive the transfer.', { site: toName }),
        actions: phone ? (
          receiveButton
        ) : (
          <>
            <Button variant="danger" disabled={!!busy} onClick={() => setConfirm('reject')}>
              {t('Reject transfer')}
            </Button>
            {receiveButton}
          </>
        ),
      };
    } else nextStep = { tone: 'waiting', text: t('In transit. Waiting for {site} to receive it.', { site: toName }) };
  } else if (statusId === TRANSFER_STATUS.Rejected) {
    nextStep = { tone: 'stopped', text: t('{site} rejected this transfer. No stock moved.', { site: toName }) };
  } else if (statusId === TRANSFER_STATUS.Cancelled) {
    nextStep = { tone: 'stopped', text: t('This transfer was cancelled. No stock moved.') };
  }

  const moreItems: MenuItem[] = [
    ...(editable && phone && dirty ? [{ key: 'discard', label: t('Discard changes'), icon: 'undo' as const, onSelect: discard }] : []),
    ...(receiving && phone ? [{ key: 'reject', label: t('Reject transfer'), icon: 'close' as const, danger: true, onSelect: () => setConfirm('reject') }] : []),
    ...(statusId === TRANSFER_STATUS.Sent && headerSite === fromId ? [{ key: 'cancel', label: t('Cancel transfer'), icon: 'close' as const, danger: true, onSelect: () => setConfirm('cancel') }] : []),
    ...(editable ? [{ key: 'delete', label: t('Delete draft'), icon: 'trash' as const, danger: true, onSelect: () => setConfirm('delete') }] : []),
  ];

  /* ---------------- Lines card ---------------- */

  const baseName = (line: TransferLine) => line.units.find((unit) => unit.isBase)?.name ?? '';
  const contextOf = (line: TransferLine): string => {
    if (editable) return line.stock !== null ? t('On hand {qty}', { qty: `${quantity(line.stock)}${baseName(line) ? ` ${baseName(line)}` : ''}` }) : line.group;
    if (statusId === TRANSFER_STATUS.Received) {
      const diff = Math.round((line.sentQty - line.receivedQty) * 10000) / 10000;
      return diff > 0 ? t('{qty} short', { qty: quantity(diff) }) : diff < 0 ? t('{qty} more than sent', { qty: quantity(-diff) }) : t('All arrived');
    }
    return line.group;
  };

  const sentMessage = (line: TransferLine) => {
    const check = sentChecks.get(line.key);
    if (!check) return null;
    if (check.error) return { tone: 'error' as const, text: t(check.error) };
    if (check.notice) return { tone: 'notice' as const, text: t(check.notice) };
    return null;
  };
  const receivedMessage = (line: TransferLine) => {
    const check = receivedChecks.get(line.key);
    if (!check) return null;
    if (check.error) return { tone: 'error' as const, text: t(check.error) };
    if (check.notice) return { tone: 'notice' as const, text: t(check.notice, { qty: quantity(check.diff) }) };
    return null;
  };

  const unitSelect = (line: TransferLine, selectId: string) => (
    <SelectField id={selectId} phone={phone} value={line.productUOMId} onChange={(event) => patchLine(line.key, (prev) => withUnit(prev, event.target.value))} wrapperStyle={phone ? { flex: 'none', width: '116px' } : undefined}>
      {!line.productUOMId && <option value="">{t('Choose a unit')}</option>}
      {line.units.map((unit) => (
        <option key={unit.id} value={unit.id}>
          {unit.name}
        </option>
      ))}
    </SelectField>
  );

  const qtyInput = (line: TransferLine, inputId: string) => {
    const message = sentMessage(line);
    return (
      <>
        <NumberField
          id={inputId}
          column="qty"
          phone={phone}
          srLabel={phone ? undefined : t('Qty for {product}', { product: line.name })}
          value={line.qty}
          invalid={message?.tone === 'error'}
          describedBy={message ? `${inputId}-msg` : undefined}
          onChange={(value) => patch(line.key, { qty: value })}
        />
        {message && (
          <FieldMessage id={`${inputId}-msg`} tone={message.tone}>
            {message.text}
          </FieldMessage>
        )}
      </>
    );
  };

  const costInput = (line: TransferLine, inputId: string) => {
    const error = sentChecks.get(line.key)?.costError ?? '';
    return (
      <>
        <NumberField
          id={inputId}
          column="cost"
          phone={phone}
          prefix="€"
          srLabel={phone ? undefined : t('Cost for {product}', { product: line.name })}
          value={line.cost}
          invalid={!!error}
          describedBy={error ? `${inputId}-msg` : undefined}
          onChange={(value) => patch(line.key, { cost: value })}
          onBlur={() => {
            const n = parseNumber(line.cost);
            if (n !== null && Number.isFinite(n)) patch(line.key, { cost: priceText(n) });
          }}
        />
        {error && (
          <FieldMessage id={`${inputId}-msg`} tone="error">
            {t(error)}
          </FieldMessage>
        )}
      </>
    );
  };
  // A cost the API gives reads as text; only one to enter is a field.
  const costValue = (line: TransferLine, phoneView = false) => (
    <ReadValue phone={phoneView} muted={line.cost.trim() === ''}>
      {line.cost.trim() === '' ? '—' : money(costOf(line))}
    </ReadValue>
  );

  const receivedInput = (line: TransferLine, inputId: string) => {
    const message = receivedMessage(line);
    return (
      <>
        <NumberField
          id={inputId}
          column="received"
          phone={phone}
          srLabel={phone ? undefined : t('Received qty for {product}', { product: line.name })}
          value={line.received}
          invalid={message?.tone === 'error'}
          describedBy={message ? `${inputId}-msg` : undefined}
          onChange={(value) => patch(line.key, { received: value })}
        />
        {message && (
          <FieldMessage id={`${inputId}-msg`} tone={message.tone}>
            {message.text}
          </FieldMessage>
        )}
      </>
    );
  };

  const desktopTable = () => {
    if (editable) {
      return (
        <div data-lines="" style={{ overflowX: 'auto' }}>
          <table style={tableStyle(720)}>
            <thead>
              <tr>
                <th scope="col" style={th('left', { first: true })}>
                  {t('Product')}
                </th>
                <th scope="col" style={th('left', { width: 124 })}>
                  {t('Unit')}
                </th>
                <th scope="col" style={th('right', { width: 104 })}>
                  {t('Qty')}
                </th>
                <th scope="col" style={th('right', { width: 104 })}>
                  {t('Cost')}
                </th>
                <th scope="col" style={th('right', { width: 90, padRight: 0 })}>
                  {t('Value')}
                </th>
                <th scope="col" style={th('left', { width: 44, padLeft: 2, padRight: 10 })}>
                  <span className="pu-sr">{t('Remove')}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {shown.map((line) => {
                const check = sentChecks.get(line.key);
                const error = !!check?.error || !!check?.costError;
                return (
                  <tr key={line.key} className="pu-row">
                    <td style={td({ first: true, error })}>
                      <ProductCell name={line.name} sku={line.sku} context={contextOf(line)} />
                    </td>
                    <td style={td({ error })}>
                      <label className="pu-sr" htmlFor={`unit-${line.key}`}>
                        {t('Unit for {product}', { product: line.name })}
                      </label>
                      {unitSelect(line, `unit-${line.key}`)}
                    </td>
                    <td style={td({ align: 'right', error })}>{qtyInput(line, `qty-${line.key}`)}</td>
                    <td style={td({ align: 'right', error })}>{line.costEditable ? costInput(line, `cost-${line.key}`) : costValue(line)}</td>
                    <td style={td({ align: 'right', padRight: 0, error })}>
                      <ReadValue strong={check?.value !== null} muted={check?.value === null}>
                        {check?.value === null || check?.value === undefined ? '—' : money(check.value)}
                      </ReadValue>
                    </td>
                    <td style={td({ align: 'right', padLeft: 2, padRight: 10, padTop: 16, error })}>
                      <IconButton icon="close" remove label={t('Remove {product}', { product: line.name })} onClick={() => removeLine(line.key)} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      );
    }
    const received = statusId === TRANSFER_STATUS.Received;
    return (
      <div data-lines="" style={{ overflowX: 'auto' }}>
        <table style={tableStyle(receiving || received ? 760 : 680)}>
          <thead>
            <tr>
              <th scope="col" style={th('left', { first: true })}>
                {t('Product')}
              </th>
              <th scope="col" style={th('left', { width: 104 })}>
                {t('Unit')}
              </th>
              <th scope="col" style={th('right', { width: 88 })}>
                {t('Sent')}
              </th>
              {(receiving || received) && (
                <th scope="col" style={th('right', { width: receiving ? 124 : 96 })}>
                  {t('Received')}
                </th>
              )}
              <th scope="col" style={th('right', { width: 104 })}>
                {t('Cost')}
              </th>
              <th scope="col" style={th('right', { width: 110, last: true })}>
                {t('Value')}
              </th>
            </tr>
          </thead>
          <tbody>
            {shown.map((line) => {
              const check = receivedChecks.get(line.key);
              const error = receiving && !!check?.error;
              const qty = receiving ? (check?.counted ? check.qty : null) : received ? line.receivedQty : line.sentQty;
              return (
                <tr key={line.key} className="pu-row">
                  <td style={td({ first: true, error })}>
                    <ProductCell name={line.name} sku={line.sku} context={contextOf(line)} />
                  </td>
                  <td style={td({ error })}>
                    <ReadValue>{unitName(line) || '—'}</ReadValue>
                  </td>
                  <td style={td({ align: 'right', error })}>
                    <ReadValue>{quantity(line.sentQty)}</ReadValue>
                  </td>
                  {receiving && <td style={td({ align: 'right', error })}>{receivedInput(line, `received-${line.key}`)}</td>}
                  {received && (
                    <td style={td({ align: 'right' })}>
                      <ReadValue>{quantity(line.receivedQty)}</ReadValue>
                    </td>
                  )}
                  <td style={td({ align: 'right', error })}>
                    <ReadValue>{money(costOf(line))}</ReadValue>
                  </td>
                  <td style={td({ align: 'right', last: true, error })}>
                    <ReadValue strong={qty !== null} muted={qty === null}>
                      {qty === null ? '—' : money(costOf(line) * qty)}
                    </ReadValue>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const phoneLines = () => (
    <div data-lines="">
      {shown.map((line) => {
        const nameId = `line-${line.key}`;
        if (editable) {
          const check = sentChecks.get(line.key);
          return (
            <PhoneLine key={line.key} labelledBy={nameId} error={!!check?.error || !!check?.costError}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                <ProductCell phone nameId={nameId} name={line.name} sku={line.sku} context={contextOf(line)} />
                <label className="pu-sr" htmlFor={`m-unit-${line.key}`}>
                  {t('Unit for {product}', { product: line.name })}
                </label>
                {unitSelect(line, `m-unit-${line.key}`)}
                <IconButton icon="close" remove phone label={t('Remove {product}', { product: line.name })} onClick={() => removeLine(line.key)} style={{ marginRight: '-16px' }} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '8px', marginTop: '10px' }}>
                <div style={{ minWidth: 0 }}>
                  <PhoneLabel htmlFor={`m-qty-${line.key}`}>{t('Qty')}</PhoneLabel>
                  {qtyInput(line, `m-qty-${line.key}`)}
                </div>
                {line.costEditable ? (
                  <div style={{ minWidth: 0 }}>
                    <PhoneLabel htmlFor={`m-cost-${line.key}`}>{t('Cost')}</PhoneLabel>
                    {costInput(line, `m-cost-${line.key}`)}
                  </div>
                ) : (
                  <div style={{ minWidth: 0, textAlign: 'right' }}>
                    <PhoneLabel align="right">{t('Cost')}</PhoneLabel>
                    {costValue(line, true)}
                  </div>
                )}
                <div style={{ minWidth: 0, textAlign: 'right' }}>
                  <PhoneLabel align="right">{t('Value')}</PhoneLabel>
                  <ReadValue phone strong={check?.value !== null} muted={check?.value === null}>
                    {check?.value === null || check?.value === undefined ? '—' : money(check.value)}
                  </ReadValue>
                </div>
              </div>
            </PhoneLine>
          );
        }
        const check = receivedChecks.get(line.key);
        const received = statusId === TRANSFER_STATUS.Received;
        const qty = receiving ? (check?.counted ? check.qty : null) : received ? line.receivedQty : line.sentQty;
        return (
          <PhoneLine key={line.key} labelledBy={nameId} error={receiving && !!check?.error}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
              <ProductCell phone nameId={nameId} name={line.name} sku={line.sku} context={contextOf(line)} />
              <p style={{ flex: 'none', fontSize: '15px', lineHeight: '22px', color: '#222525' }}>
                <span className="pu-sr">{t('Unit')}: </span>
                {unitName(line)}
              </p>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '8px', marginTop: '10px', textAlign: 'right' }}>
              <div>
                <PhoneLabel align="right">{t('Sent')}</PhoneLabel>
                <ReadValue phone>{quantity(line.sentQty)}</ReadValue>
              </div>
              <div style={{ minWidth: 0 }}>
                {receiving ? (
                  <>
                    <PhoneLabel htmlFor={`m-received-${line.key}`} align="right">
                      {t('Received')}
                    </PhoneLabel>
                    {receivedInput(line, `m-received-${line.key}`)}
                  </>
                ) : (
                  <>
                    <PhoneLabel align="right">{received ? t('Received') : t('Cost')}</PhoneLabel>
                    <ReadValue phone>{received ? quantity(line.receivedQty) : money(costOf(line))}</ReadValue>
                  </>
                )}
              </div>
              <div>
                <PhoneLabel align="right">{t('Value')}</PhoneLabel>
                <ReadValue phone strong={qty !== null} muted={qty === null}>
                  {qty === null ? '—' : money(costOf(line) * qty)}
                </ReadValue>
              </div>
            </div>
          </PhoneLine>
        );
      })}
    </div>
  );

  const linesBody = () => {
    if (loading && !transfer) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', padding: phone ? '16px' : '20px' }}>
          {[0, 1, 2].map((index) => (
            <Skeleton key={index} height={40} />
          ))}
        </div>
      );
    }
    if (lines.length === 0) {
      return (
        <div style={{ padding: phone ? '16px' : '20px' }}>
          {editable ? (
            <EmptyState
              phone={phone}
              title={t('No products on this transfer yet')}
              desc={t('Use Add products to choose what {site} sends.', { site: fromName })}
              action={
                <Button variant="primary" phone={phone} icon="plus" onClick={openPicker}>
                  {t('Add products')}
                </Button>
              }
            />
          ) : (
            <EmptyState phone={phone} title={t('No products on this transfer')} />
          )}
        </div>
      );
    }
    if (shown.length === 0) {
      return (
        <div style={{ padding: phone ? '16px' : '20px' }}>
          <EmptyState
            icon="search"
            phone={phone}
            title={t('No products on this transfer match "{search}". Check the spelling or search by SKU.', { search: lineSearch.trim() })}
            action={
              <Button variant="text" phone={phone} onClick={() => setLineSearch('')}>
                {t('Clear search')}
              </Button>
            }
          />
        </div>
      );
    }
    return phone ? phoneLines() : desktopTable();
  };

  /* ---------------- Save bar ---------------- */

  const changeText = (change: { kind: string; key: string }) => {
    const line = (change.kind === 'removed' ? saved : lines).find((other) => other.key === change.key);
    const name = line?.name ?? '';
    return change.kind === 'added' ? t('Added {name}', { name }) : change.kind === 'removed' ? t('Removed {name}', { name }) : t('Edited {name}', { name });
  };
  const attentionText = attention ? (attention === 1 ? t('1 line needs attention') : t('{count} lines need attention', { count: attention })) : null;

  const editBar = editable && transfer && (
    <SaveBar
      phone={phone}
      label={t('Unsaved changes')}
      summary={{
        lead: dirty ? (changes.length === 1 ? t('1 unsaved change') : t('{count} unsaved changes', { count: changes.length })) : t('No unsaved changes'),
        detail: dirty ? [changes.length > 1 ? t('{first}, +{count} more', { first: changeText(changes[0]), count: changes.length - 1 }) : changeText(changes[0])] : [],
        attention: notice || attentionText,
        quiet: !dirty,
      }}
      secondary={
        phone ? (
          <Button variant="text" phone disabled={!dirty || !!busy} onClick={() => save(false)}>
            {busy === 'draft' ? t('Saving…') : t('Save draft')}
          </Button>
        ) : (
          <>
            <Button variant="text" disabled={!dirty || !!busy} onClick={discard}>
              {t('Discard changes')}
            </Button>
            <Button disabled={!dirty || !!busy} aria-busy={busy === 'draft' || undefined} onClick={() => save(false)}>
              {busy === 'draft' ? t('Saving…') : t('Save draft')}
            </Button>
          </>
        )
      }
      primary={
        <Button variant="primary" phone={phone} fullWidth={phone} disabled={lines.length === 0 || !!busy} aria-busy={busy === 'send' || undefined} onClick={() => save(true)}>
          {busy === 'send' ? t('Sending…') : t('Send transfer')}
        </Button>
      }
    />
  );

  // Once counting starts, the bar keeps the count and the Receive button in reach under long lists.
  const receiveBar = receiving && dirty && (
    <SaveBar
      phone={phone}
      label={t('Receiving')}
      summary={{
        lead: t('{counted} of {count} lines counted', { counted: counted.length, count: lines.length }),
        detail: shortLines.length ? [shortLines.length === 1 ? t('1 line short') : t('{count} lines short', { count: shortLines.length })] : [],
        attention: attentionText,
      }}
      secondary={
        phone ? undefined : (
          <Button variant="text" disabled={!!busy} onClick={discard}>
            {t('Clear counts')}
          </Button>
        )
      }
      primary={
        <Button variant="primary" phone={phone} fullWidth={phone} disabled={!!busy} onClick={startReceive}>
          {t('Receive transfer')}
        </Button>
      }
    />
  );

  const printDoc = useTransferPrint(
    transfer,
    saved.map((line) => ({ sku: line.sku, productName: line.name, uomName: unitName(line), sentQty: line.sentQty, receivedQty: line.receivedQty })),
  );

  const number = `#${transfer?.internal_number ?? ''}`;
  const shortCount = shortLines.length;

  return (
    <>
      {printDoc.node}
      <PageFrame phone={phone} withPhoneBar={editable || (receiving && dirty)}>
        {failed && !transfer ? (
          <ErrorBanner message={t('The transfer could not be loaded.')} retryLabel={t('Try again')} onRetry={load} />
        ) : (
          <>
            {transfer ? (
              <DocumentHeader
                phone={phone}
                t={t}
                backLabel={t('Back to transfers')}
                onBack={onBack}
                title={title}
                status={transferStatusOf(statusId)}
                nextStep={nextStep}
                facts={facts}
                onPrint={statusId >= TRANSFER_STATUS.Sent ? printDoc.print : undefined}
                onDownload={statusId >= TRANSFER_STATUS.Sent ? printDoc.download : undefined}
                moreItems={moreItems}
              />
            ) : (
              <section style={{ ...card, display: 'flex', flexDirection: 'column', gap: '16px', padding: phone ? '16px' : '20px' }}>
                <Skeleton width="120px" />
                <Skeleton width="220px" height={28} />
                <Skeleton height={96} />
              </section>
            )}
            <LinesCard
              phone={phone}
              titleId="pu-lines-title"
              title={t('Products')}
              count={transfer ? lines.length : undefined}
              search={lines.length > 0 ? { label: t('Find a product in this transfer'), placeholder: t('Find a product in this transfer'), value: lineSearch, onChange: setLineSearch } : undefined}
              actions={
                editable && lines.length > 0 ? (
                  <Button phone={phone} icon="plus" onClick={openPicker}>
                    {t('Add products')}
                  </Button>
                ) : receiving && lines.length > 0 ? (
                  <Button phone={phone} icon="check" onClick={everythingArrived}>
                    {t('Everything arrived')}
                  </Button>
                ) : undefined
              }
              footer={lines.length > 0 && <TotalsList phone={phone} rows={totalRows} />}
            >
              {linesBody()}
            </LinesCard>
            {editBar}
            {receiveBar}
          </>
        )}
      </PageFrame>
      {editable && (
        <ProductPicker
          open={pickerOpen}
          phone={phone}
          t={t}
          title={t('Add products to {transfer}', { transfer: title })}
          search={pickSearch}
          onSearch={setPickSearch}
          groups={[]}
          groupId=""
          onGroup={() => undefined}
          rows={pickerRows}
          loading={picksLoading}
          hasMore={picks.length < pickCount}
          onMore={() => loadPicks(pickPage + 1, true)}
          onToggle={togglePick}
          onUnit={pickUnit}
          selectedCount={selected.size}
          onCancel={() => setPickerOpen(false)}
          onAdd={addPicked}
          takenLabel={t('On this transfer')}
          unitLabel={(product) => t('Unit for {product}', { product })}
          searchPlaceholder={t('Name or SKU')}
        />
      )}
      <ConfirmDialog
        open={confirm === 'receive'}
        phone={phone}
        busy={busy === 'receive'}
        title={t('Receive transfer {number}?', { number })}
        body={
          <>
            {t('Stock moves from {from} to {to} as counted.', { from: fromName, to: toName })}{' '}
            {shortCount > 0 && (shortCount === 1 ? t('1 line arrived short; what did not arrive stays at {from}.', { from: fromName }) : t('{count} lines arrived short; what did not arrive stays at {from}.', { count: shortCount, from: fromName }))}{' '}
            {t('This cannot be undone.')}
          </>
        }
        confirmLabel={t('Receive transfer')}
        cancelLabel={t('Keep counting')}
        closeLabel={t('Close')}
        onConfirm={receive}
        onCancel={() => setConfirm('')}
      />
      <ConfirmDialog
        open={confirm === 'reject'}
        phone={phone}
        danger
        busy={busy === 'reject'}
        title={t('Reject transfer {number}?', { number })}
        body={t('The transfer is marked rejected and no stock moves at {from} or {to}. This cannot be undone.', { from: fromName, to: toName })}
        confirmLabel={t('Reject transfer')}
        cancelLabel={t('Cancel')}
        closeLabel={t('Close')}
        onConfirm={() => close('reject')}
        onCancel={() => setConfirm('')}
      />
      <ConfirmDialog
        open={confirm === 'cancel'}
        phone={phone}
        danger
        busy={busy === 'cancel'}
        title={t('Cancel transfer {number}?', { number })}
        body={t('The transfer is marked cancelled and no stock moves. This cannot be undone.')}
        confirmLabel={t('Cancel transfer')}
        cancelLabel={t('Keep transfer')}
        closeLabel={t('Close')}
        onConfirm={() => close('cancel')}
        onCancel={() => setConfirm('')}
      />
      <ConfirmDialog
        open={confirm === 'delete'}
        phone={phone}
        danger
        busy={busy === 'delete'}
        title={t('Delete draft transfer {number}?', { number })}
        body={t('The draft and its products are removed. This cannot be undone.')}
        confirmLabel={t('Delete draft')}
        cancelLabel={t('Keep draft')}
        closeLabel={t('Close')}
        onConfirm={removeDraft}
        onCancel={() => setConfirm('')}
      />
      <ConfirmDialog
        open={confirm === 'leave'}
        phone={phone}
        danger
        title={t('Leave without saving?')}
        body={receiving ? t('The quantities you counted are not saved.') : t('Your changes to this transfer are not saved.')}
        confirmLabel={t('Leave without saving')}
        cancelLabel={t('Keep editing')}
        closeLabel={t('Close')}
        onConfirm={() => {
          setConfirm('');
          backToList();
        }}
        onCancel={() => setConfirm('')}
      />
    </>
  );
};

export default TransferDetail;
